import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, ChevronLeft, ChevronRight, Volume2, VolumeX } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

/**
 * Iter-27.64 — Mode "Stories Instagram" pour /espace-partenaires/:id.
 *
 * Enchaîne automatiquement les messages de remerciement vidéo selfie / photos
 * en plein écran (5s par image, durée réelle par vidéo). Swipe latéral,
 * progress bar segmentée, et réactions emoji envoyées en retour à l'asso.
 */
const IMAGE_DURATION_MS = 5000; // 5s par image
const ALLOWED_EMOJIS = ["❤️", "🙏", "✨", "💪", "🔥", "👏"];

export default function GratitudeStoriesViewer({ items, association, onClose }) {
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [muted, setMuted] = useState(false);
    const [progress, setProgress] = useState(0); // 0..1
    const [sentReactions, setSentReactions] = useState({}); // {news_id: emoji}
    const startedAtRef = useRef(Date.now());
    const elapsedAtPauseRef = useRef(0);
    const videoRef = useRef(null);
    // Iter-27.72 — Refs pour stabiliser les callbacks et éviter les re-créations
    // de l'interval d'auto-advance qui empêchaient la progress bar de défiler.
    const itemsLengthRef = useRef(items.length);
    const onCloseRef = useRef(onClose);
    const readMarkedRef = useRef(new Set());
    useEffect(() => {
        itemsLengthRef.current = items.length;
        onCloseRef.current = onClose;
    }, [items.length, onClose]);

    const current = items[index];
    const isVideo = current?.image_url
        ? /\.(mp4|webm|mov)(\?|$)/i.test(current.image_url)
        : false;

    // Iter-27.72 — `next` et `prev` STABLES (deps vides) qui lisent les refs.
    // Avant : `next` dépendait de `isLast` qui dépendait de `index` → recréé à chaque
    // setState → useEffect d'auto-advance se remontait en boucle → progress bloquée.
    const next = useCallback(() => {
        setIndex((i) => {
            const total = itemsLengthRef.current;
            if (i >= total - 1) {
                // Dernière story : on ferme le viewer
                queueMicrotask(() => onCloseRef.current?.());
                return i;
            }
            return i + 1;
        });
        setProgress(0);
        startedAtRef.current = Date.now();
        elapsedAtPauseRef.current = 0;
    }, []);

    const prev = useCallback(() => {
        setIndex((i) => Math.max(0, i - 1));
        setProgress(0);
        startedAtRef.current = Date.now();
        elapsedAtPauseRef.current = 0;
    }, []);

    // Iter-27.72 — Marque automatiquement comme "lu" l'item courant après ~1s d'affichage.
    // Idempotent grâce à readMarkedRef.
    useEffect(() => {
        if (!current?.news_id) return;
        const nid = current.news_id;
        const t = setTimeout(() => {
            if (readMarkedRef.current.has(nid)) return;
            readMarkedRef.current.add(nid);
            api.post(`/news/${nid}/read`).catch(() => {});
        }, 800);
        return () => clearTimeout(t);
    }, [current?.news_id]);

    // Auto-advance pour les images (5s) — deps minimisées pour ne pas se remonter en boucle
    useEffect(() => {
        if (isVideo || paused || !current) return;
        startedAtRef.current = Date.now() - elapsedAtPauseRef.current;
        const id = setInterval(() => {
            const elapsed = Date.now() - startedAtRef.current;
            const ratio = Math.min(1, elapsed / IMAGE_DURATION_MS);
            setProgress(ratio);
            if (ratio >= 1) {
                next();
            }
        }, 80);
        return () => clearInterval(id);
    }, [index, isVideo, paused, current, next]);

    // Vidéo : sync progress avec timeupdate
    useEffect(() => {
        if (!isVideo || !videoRef.current) return;
        const v = videoRef.current;
        const handler = () => {
            if (v.duration > 0) {
                setProgress(v.currentTime / v.duration);
            }
        };
        const onEnd = () => next();
        v.addEventListener("timeupdate", handler);
        v.addEventListener("ended", onEnd);
        return () => {
            v.removeEventListener("timeupdate", handler);
            v.removeEventListener("ended", onEnd);
        };
    }, [isVideo, index, next]);

    // Iter-27.64 — Bloquer le scroll du body et masquer le bottom-nav pour
    // une expérience full-screen immersive façon Stories Instagram.
    useEffect(() => {
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        document.body.classList.add("stories-viewer-open");
        return () => {
            document.body.style.overflow = prevOverflow;
            document.body.classList.remove("stories-viewer-open");
        };
    }, []);

    // Keyboard navigation
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight") next();
            else if (e.key === "ArrowLeft") prev();
            else if (e.key === "Escape") onClose?.();
            else if (e.key === " ") {
                e.preventDefault();
                setPaused((p) => !p);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [next, prev, onClose]);

    // Pause toggle for video
    useEffect(() => {
        if (!isVideo || !videoRef.current) return;
        if (paused) {
            videoRef.current.pause();
            elapsedAtPauseRef.current = Date.now() - startedAtRef.current;
        } else {
            videoRef.current.play().catch(() => {});
        }
    }, [paused, isVideo]);

    // Tap zones — pas de swipe gesture custom, on utilise les zones cliquables
    const handleTap = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const third = rect.width / 3;
        if (x < third) prev();
        else if (x > 2 * third) next();
        else setPaused((p) => !p);
    };

    const sendReaction = async (emoji) => {
        if (sentReactions[current.news_id]) {
            toast.info("Tu as déjà envoyé une réaction sur ce message");
            return;
        }
        try {
            await api.post(`/news/${current.news_id}/react`, { emoji });
            setSentReactions((s) => ({ ...s, [current.news_id]: emoji }));
            toast.success(`${emoji} envoyé à ${association.name}`);
            // Auto-advance après réaction (UX Insta-like)
            setTimeout(() => next(), 600);
        } catch (e) {
            toast.error("Erreur lors de l'envoi");
        }
    };

    if (!current) return null;

    const sentEmoji = sentReactions[current.news_id];
    const accent = association.primary_color || "#FCDB53";

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
                data-testid="gratitude-stories-viewer"
            >
                {/* Background container */}
                <div className="relative w-full h-full max-w-[500px] max-h-screen bg-shariti-black overflow-hidden">
                    {/* Progress bars (segmented) */}
                    <div className="absolute top-0 left-0 right-0 z-30 flex gap-1 p-3 pt-4">
                        {items.map((_, i) => (
                            <div
                                key={i}
                                className="flex-1 h-0.5 bg-white/25 rounded-full overflow-hidden"
                            >
                                <div
                                    className="h-full bg-white transition-all duration-100"
                                    style={{
                                        width: `${
                                            i < index
                                                ? 100
                                                : i === index
                                                  ? progress * 100
                                                  : 0
                                        }%`,
                                    }}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Top bar : avatar asso + close */}
                    <div className="absolute top-7 left-0 right-0 z-30 flex items-center gap-3 p-3">
                        {association.logo_url && (
                            <div className="w-9 h-9 rounded-full bg-white p-1 shrink-0">
                                <img
                                    src={association.logo_url}
                                    alt={association.name}
                                    className="w-full h-full object-contain"
                                />
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-extrabold text-white truncate">
                                {association.name}
                            </p>
                            <p className="text-[10px] text-white/55 font-light">
                                Message à toi
                            </p>
                        </div>
                        {isVideo && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setMuted((m) => !m);
                                }}
                                className="p-2 rounded-full bg-white/10 backdrop-blur-md"
                                aria-label="Mute"
                            >
                                {muted ? (
                                    <VolumeX className="w-4 h-4 text-white" />
                                ) : (
                                    <Volume2 className="w-4 h-4 text-white" />
                                )}
                            </button>
                        )}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose?.();
                            }}
                            data-testid="stories-close"
                            className="p-2 rounded-full bg-white/10 backdrop-blur-md"
                            aria-label="Fermer"
                        >
                            <X className="w-5 h-5 text-white" />
                        </button>
                    </div>

                    {/* Media area + tap zones */}
                    <div
                        className="absolute inset-0 cursor-pointer"
                        onClick={handleTap}
                        data-testid="stories-tap-area"
                    >
                        {/* Media */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black">
                            {current.image_url ? (
                                isVideo ? (
                                    <video
                                        key={current.news_id}
                                        ref={videoRef}
                                        src={current.image_url}
                                        autoPlay
                                        muted={muted}
                                        playsInline
                                        className="w-full h-full object-contain"
                                        data-testid={`stories-video-${current.news_id}`}
                                    />
                                ) : (
                                    <img
                                        src={current.image_url}
                                        alt={current.title}
                                        className="w-full h-full object-cover"
                                        data-testid={`stories-image-${current.news_id}`}
                                    />
                                )
                            ) : (
                                // Fond gradient si pas de média
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: `linear-gradient(135deg, ${accent}88 0%, ${accent}33 60%, rgba(24,24,24,0.95) 100%)`,
                                    }}
                                />
                            )}
                        </div>

                        {/* Overlay gradient pour lisibilité texte */}
                        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black via-black/60 to-transparent pointer-events-none" />
                        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/60 to-transparent pointer-events-none" />

                        {/* Texte du message */}
                        <div className="absolute bottom-32 left-0 right-0 p-6 pointer-events-none z-20">
                            <motion.div
                                key={current.news_id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: 0.15 }}
                            >
                                <h2 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight mb-3 drop-shadow-lg">
                                    {current.title}
                                </h2>
                                <p className="text-sm sm:text-base text-white/90 font-light leading-relaxed line-clamp-4 drop-shadow">
                                    {current.body}
                                </p>
                            </motion.div>
                        </div>

                        {/* Pause indicator centré */}
                        {paused && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-16 h-16 rounded-full bg-black/60 flex items-center justify-center backdrop-blur-md">
                                    <Play className="w-8 h-8 text-white ml-1" fill="white" />
                                </div>
                            </div>
                        )}

                        {/* Navigation arrows desktop (hidden mobile) */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                prev();
                            }}
                            disabled={index === 0}
                            className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur items-center justify-center disabled:opacity-30 z-20"
                            aria-label="Précédent"
                            data-testid="stories-prev"
                        >
                            <ChevronLeft className="w-6 h-6 text-white" />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                next();
                            }}
                            className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur items-center justify-center z-20"
                            aria-label="Suivant"
                            data-testid="stories-next"
                        >
                            <ChevronRight className="w-6 h-6 text-white" />
                        </button>
                    </div>

                    {/* Réactions emoji (bottom) */}
                    <div className="absolute bottom-4 left-0 right-0 z-30 px-4">
                        <div className="flex items-center justify-center gap-2 bg-black/55 backdrop-blur-md rounded-full p-2 max-w-md mx-auto">
                            {ALLOWED_EMOJIS.map((emoji) => {
                                const isSent = sentEmoji === emoji;
                                return (
                                    <button
                                        key={emoji}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            sendReaction(emoji);
                                        }}
                                        disabled={!!sentEmoji}
                                        data-testid={`stories-react-${emoji}`}
                                        className={`text-2xl w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                                            isSent
                                                ? "bg-shariti-orange scale-110"
                                                : sentEmoji
                                                  ? "opacity-30"
                                                  : "hover:bg-white/10 hover:scale-110 active:scale-95"
                                        }`}
                                        aria-label={`Réagir ${emoji}`}
                                    >
                                        {emoji}
                                    </button>
                                );
                            })}
                        </div>
                        {sentEmoji && (
                            <motion.p
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-center text-[11px] font-bold text-shariti-yellow mt-2"
                            >
                                {sentEmoji} envoyé à {association.name}
                            </motion.p>
                        )}
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
}
